import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { Steps, Button, Spin, Alert, Modal } from 'antd';
import { CarOutlined, AppstoreOutlined, FileTextOutlined } from '@ant-design/icons';
import TopBar from '../TopBar/TopBar';
import { getFirebaseAuth } from '../../services/firebase';
import { PPI_API } from '../../services/constants';
import { Feature } from './FeatureCard/FeatureCard';
import { VehicleDetailsForm } from './components/VehicleDetailsForm';
import { FeatureSelection } from './components/FeatureSelection';
import { CommonIssuesResearch } from './components/CommonIssuesResearch';
import { MarketInsightsResearch } from './components/MarketInsightsResearch';
import { CommonIssuesData, MarketInsightsData } from '../../types/api';
import { authPpiApiAxiosWithRefresh } from '../../services/api';

const { Step } = Steps;

const Research: React.FC = () => {
    const [searchParams] = useSearchParams();
    const { step = 'details' } = useParams<{ step: string }>();
    const docId = searchParams.get('docId');
    const navigate = useNavigate();

    const [reportPage, setReportPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [carDetails, setCarDetails] = useState<any>({});
    const [features, setFeatures] = useState<{ [category: string]: Feature[] }>({});
    const [additionalRequirements, setAdditionalRequirements] = useState<string>('');
    const [reportStatus, setReportStatus] = useState<string>('');
    const [researchResults, setResearchResults] = useState<{
        common_issues?: { structured_data: CommonIssuesData; citations: string[] };
        market_insights?: { structured_data: MarketInsightsData; citations: string[] };
    }>({});
    const [error, setError] = useState<string | null>(null);
    const [statusMessage, setStatusMessage] = useState<string>('');
    const [commonIssuesLoading, setCommonIssuesLoading] = useState(false);
    const [marketInsightsLoading, setMarketInsightsLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const setStep = (stepName: string) => {
        navigate(`/research/${stepName}?docId=${docId || ''}`);
    };

    const loadResearchData = useCallback(async () => {
        if (!docId) return;
        setLoading(true);
        try {
            const response = await (await authPpiApiAxiosWithRefresh()).get(`/research-report/${docId}`);
            if (response.data.success) {
                const { vehicle_details, features, additional_requirements, research_report_status, research_results } = response.data.data;
                setCarDetails(vehicle_details);
                setFeatures(features);
                if (additional_requirements) {
                    setAdditionalRequirements(additional_requirements);
                }
                if (research_report_status) {
                    setReportStatus(research_report_status);
                }
                if (research_results) {
                    setResearchResults(research_results);
                }
            } else {
                setError(response.data.error || 'Failed to load research data.');
            }
        } catch (err) {
            setError('Error loading research data.');
            console.error('Error loading research data:', err);
        } finally {
            setLoading(false);
        }
    }, [docId]);

    useEffect(() => {
        if (docId && !carDetails.Make) { // Only load if docId exists and carDetails are not already loaded
            loadResearchData();
        }
    }, [docId, carDetails.Make, loadResearchData]);

    // Effect to handle window resize for mobile view detection
    React.useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Step 1: Fetch Features
    const onFinishStep1 = async (values: any) => {
        console.log('Step 1 values: ', values);
        setLoading(true);
        setError(null);
        setStatusMessage('Fetching vehicle features...');
        setCarDetails(values);

        try {
            const currentUser = getFirebaseAuth().currentUser;
            if (!currentUser) throw new Error('No authenticated user found');
            const token = await currentUser.getIdToken();

            const response = await fetch(`${PPI_API}/api/car-features`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(values),
            });

            const data = await response.json();

            if (data.error) {
                throw new Error(data.error);
            }

            if (data.success && data.features) {
                navigate(`/research/features?docId=${data.doc_id}`);
                setFeatures(data.features);
            }

        } catch (err: any) {
            console.error('Error fetching features:', err);
            setError(err.message || 'Failed to fetch vehicle features');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            if (docId && carDetails.Make) { // Only update if we have a docId and data is loaded
                const updateSelections = async () => {
                    try {
                        const payload = {
                            features,
                            additionalRequirements,
                        };
                        await (await authPpiApiAxiosWithRefresh()).put(`/research-report/${docId}/features`, payload);
                    } catch (err) {
                        console.error('Failed to update selections:', err);
                        // Maybe show a toast notification here
                    }
                };
                updateSelections();
            }
        }, 1000); // Debounce time of 1 second

        return () => clearTimeout(timer);
    }, [features, additionalRequirements, docId, carDetails.Make]);

    const handleStatusChange = (newStatus: 'added' | 'pending', category: string, featureText: string) => {

        setFeatures(prev => {

            const newFeatures = { ...prev };

            const categoryFeatures = [...(newFeatures[category] || [])];

            const existingIndex = categoryFeatures.findIndex(f => f.text === featureText);



            if (existingIndex >= 0) {

                categoryFeatures[existingIndex] = {

                    ...categoryFeatures[existingIndex],

                    selected: newStatus === 'added'

                };

            }



            newFeatures[category] = categoryFeatures;

            return newFeatures;

        });

    };



    const stepOrder = ['details', 'features', 'report'];

    const currentStepIndex = stepOrder.indexOf(step);



    // Step 2: Generate Research Report

    const onFinishStep2 = async () => {
        setLoading(true);
        setCommonIssuesLoading(true);
        setMarketInsightsLoading(true);
        setError(null);
        setResearchResults({});
        setStatusMessage('Starting research...');
        setStep('report'); // Move to results view immediately to show progress

        try {
            const currentUser = getFirebaseAuth().currentUser;
            if (!currentUser) throw new Error('No authenticated user found');
            const token = await currentUser.getIdToken();

            const payload = {
                docId: docId,
            };

            const response = await fetch(`${PPI_API}/api/create-research-report`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(payload),
            });



            if (!response.body) throw new Error('Response body is null');



            const reader = response.body.getReader();

            const decoder = new TextDecoder();



            const processMessage = (message: string) => {

                if (message.startsWith('data:')) {

                    try {

                        const data = JSON.parse(message.substring(5));

                        const { step, status, data: stepData, error: stepError } = data;



                        if (data.message) setStatusMessage(data.message);



                        if (status === 'error') {

                            setError(stepError || 'An error occurred');

                        }



                        if (step === 'common_issues') {

                            setCommonIssuesLoading(false);

                            if (status === 'completed') {

                                setResearchResults(prev => ({ ...prev, common_issues: stepData }));

                            }

                        }

                        if (step === 'market_insights') {

                            setMarketInsightsLoading(false);

                            if (status === 'completed') {

                                setResearchResults(prev => ({ ...prev, market_insights: stepData }));

                            }

                        }



                        if (step === 'complete') {

                            setLoading(false);

                            setStatusMessage('Research complete!');
                            setReportStatus('completed');

                        }

                    } catch (e) {

                        console.error('Failed to parse JSON:', message);

                    }

                }

            };



            let buffer = '';

            while (true) {

                const { done, value } = await reader.read();

                if (done) {

                    if (buffer) processMessage(buffer);

                    break;

                }



                buffer += decoder.decode(value, { stream: true });

                const messages = buffer.split('\n\n');

                buffer = messages.pop() || '';

                messages.forEach(processMessage);

            }



        } catch (err: any) {

            console.error('Research error:', err);

            setError(err.message || 'Failed to generate research report');

            setLoading(false);

        }

    };

    const handleGenerateReport = () => {
        if (reportStatus === 'completed') {
            Modal.confirm({
                title: 'Are you sure you want to rerun your report?',
                content: 'The previous results will be lost.',
                onOk() {
                    onFinishStep2();
                },
            });
        } else {
            onFinishStep2();
        }
    };



    return (

        <div>

            <TopBar className="title" title="Research" />

            <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>

                {!isMobile && (
                    <Steps current={currentStepIndex} style={{ marginBottom: 40 }}>
                        <Step title="Vehicle Details" icon={<CarOutlined />} />
                        <Step title="Select Features" icon={<AppstoreOutlined />} />
                        <Step title="Research Report" icon={<FileTextOutlined />} />
                    </Steps>
                )}



                {/* Step 1: Vehicle Input */}

                {step === 'details' && (

                    <VehicleDetailsForm
                        onFinish={onFinishStep1}
                        loading={loading}
                        initialValues={carDetails}
                        isEditing={!!docId}
                        onContinue={() => setStep('features')}
                    />

                )}



                {/* Step 2: Feature Selection */}

                {step === 'features' && (

                    <FeatureSelection
                        features={features}
                        carDetails={carDetails}
                        additionalRequirements={additionalRequirements}
                        onAdditionalRequirementsChange={(e) => setAdditionalRequirements(e.target.value)}
                        onStatusChange={handleStatusChange}
                        onBack={() => setStep('details')}
                        onGenerate={handleGenerateReport}
                        reportStatus={reportStatus}
                        onViewReport={() => setStep('report')}
                    />

                )}

                {/* Step 3: Results */}
                {step === 'report' && (
                    <div>
                        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between' }}>
                            {reportPage === 0 && (
                                <Button onClick={() => setStep('features')} disabled={loading}>Back to Feature Selection</Button>
                            )}
                            {reportPage === 1 && (
                                <Button onClick={() => setReportPage(0)}>Back to Common Issues</Button>
                            )}
                            {reportPage === 0 && (
                                <Button type="primary" onClick={() => setReportPage(1)} disabled={!researchResults.market_insights?.structured_data}>Market Insights</Button>
                            )}
                        </div>

                        {error && (
                            <Alert
                                message="Error"
                                description={error}
                                type="error"
                                showIcon
                                style={{ marginBottom: 24 }}
                            />
                        )}

                        {reportPage === 0 && (
                            commonIssuesLoading ? (
                                <div style={{ textAlign: 'center', margin: '40px 0' }}>
                                    <Spin size="large" />
                                    <div style={{ marginTop: 16, fontSize: 16 }}>{statusMessage}</div>
                                </div>
                            ) : (
                                researchResults.common_issues && researchResults.common_issues.structured_data && (
                                    <CommonIssuesResearch commonIssues={researchResults.common_issues.structured_data} />
                                )
                            )
                        )}

                        {reportPage === 1 && (
                            marketInsightsLoading ? (
                                <div style={{ textAlign: 'center', margin: '40px 0' }}>
                                    <Spin size="large" />
                                    <div style={{ marginTop: 16, fontSize: 16 }}>{statusMessage}</div>
                                </div>
                            ) : (
                                researchResults.market_insights && researchResults.market_insights.structured_data && (
                                    <MarketInsightsResearch marketInsights={researchResults.market_insights.structured_data} />
                                )
                            )
                        )}

                        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
                            {reportPage === 0 && (
                                <Button onClick={() => setStep('features')} disabled={loading}>Back to Feature Selection</Button>
                            )}
                            {reportPage === 1 && (
                                <Button onClick={() => setReportPage(0)}>Back to Common Issues</Button>
                            )}
                            {reportPage === 0 && (
                                <Button type="primary" onClick={() => setReportPage(1)} disabled={!researchResults.market_insights?.structured_data}>Market Insights</Button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Research;